import React, { useCallback, useEffect, useRef, useState } from "react";
import Vimeo from "@u-wave/react-vimeo";
import {primaryInput} from 'detect-it';
import './showreel.css';
import { AnimatePresence, motion } from "framer-motion";
import debounce from "lodash.debounce";
import { controlVariants } from "./variants";

const Showreel = ({videoLink, isVisible, setIsVisible, isShowreelVisibleRef}) => {
    const [playVideo, setPlayVideo] = useState(false);
    const [volume, setVolume] = useState(1);
    const [timestamp, setTimestamp] = useState(0);
    const [videoProgress, setVideoProgress] = useState(0);
    const [showControls, setShowControls] = useState(primaryInput == "touch")
    const wrapperRef = useRef(null);
    const exitToggleRef = useRef(null);
    const [videoDuration, setVideoDuration] = useState(-1);
    const [videoTime, setVideoTime] = useState(0);
    const startTimeRef = useRef(0);
    const [cachedElapedTime, setCachedElapsedTime] = useState(0);
    const animationFrameRef = useRef(null); 
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [durationTimestamp, setDurationTimestamp] = useState(0);
    const playerRef = useRef(null);
    const videoWrapperRef = useRef(null);
    const showreelControllerRef = useRef(null)

    useEffect(() => {
        if(videoDuration > 0) setDurationTimestamp(convertTimestamp(videoDuration));
    }, [videoDuration])

    const handlePlayingStart = () => {
        setIsVideoPlaying(true);
    }

    const handlePlayingStopped = () => {
        setIsVideoPlaying(false);
    }   

    const convertTimestamp = (time) => {
        let minutes = Math.floor(time/60);
        let seconds = Math.floor(time - (minutes*60));
        let ms = (time % 1).toFixed(3) * 1000;
        if(ms  == 0) ms = '000';
        else if(ms < 10) ms = '00'+ms;
        else if(ms < 100) ms = '0'+ms;
        if(minutes < 10) minutes = '0'+minutes;
        if(seconds < 10) seconds = '0'+seconds;
        return (minutes+":"+seconds+"."+ms)
    }


    useEffect(() => {
        if(isVideoPlaying && videoDuration > 0){
            startTimeRef.current = Date.now();
            animationFrameRef.current = requestAnimationFrame(updateTimer)
        } else{
            setCachedElapsedTime(videoTime);
            setVideoProgress(videoTime/videoDuration);
            cancelAnimationFrame(animationFrameRef.current);
        }
    }, [isVideoPlaying, videoDuration]);

    const handleTimeUpdate = (e) => {
        let seconds = e.seconds;
        let percent = e.percent;
        if(videoDuration == -1){setVideoDuration(e.duration)}
        setVideoTime(e.seconds);
    }

    const updateTimer = () => {
        // Calculate the elapsed time by checking the difference between current time and start time
        const currentTime = Date.now();
        const timeElapsed = (currentTime - startTimeRef.current) / 1000; // Convert from ms to seconds

        // Update the state with the new elapsed time
        setVideoProgress((cachedElapedTime + timeElapsed)/videoDuration);
        setTimestamp(convertTimestamp(cachedElapedTime + timeElapsed));

        // Request the next animation frame if the video is still playing
        if (isVideoPlaying) {
            animationFrameRef.current = requestAnimationFrame(updateTimer);
        } else{
            setVideoProgress(videoTime/videoDuration);
        }
    };

    useEffect(() => {
        if(isVisible){
            try{
                wrapperRef.current.style.opacity = 1;
                setTimeout(()=> {
                    videoWrapperRef.current.style.opacity = 1;
                    showreelControllerRef.current.style.opacity = 1;
                }, 1000)
            }catch{}
            setPlayVideo(true);
        } else{
            try{
                videoWrapperRef.current.style.opacity = 0;
                showreelControllerRef.current.style.opacity = 0;
                setTimeout(()=> {
                    wrapperRef.current.style.opacity = 0
                }, 500)
            }catch{}
            setPlayVideo(false);
        }
    }, [isVisible])

    const handleVideoClick = () => {
        setPlayVideo(!playVideo)
    }

    const debounceHideControls = useCallback(
        debounce(() => {
            setShowControls(false)
          }, 1000),
        []
    );

    const handleSoundClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        let tmp = 0;
        if(volume){
            tmp = volume - 1;
        } else{
            tmp = 1
        }
        setVolume(tmp);
    }

    const handleMouseMove = () => {
        setShowControls(true);
        debounceHideControls.cancel();
        debounceHideControls();
    }

    const handleExit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setVideoProgress(0);
        setTimestamp("00:00.000");
        setIsVisible(false);
        isShowreelVisibleRef.current = false
        try{
            playerRef.current.setCurrentTime(0)
        }catch{}
    }

    return(
        <>
            <div 
                className={`fullscreen_video__wrapper ${isVisible ? '' : 'no-pe'}`}
                ref={wrapperRef}
                onClick={handleVideoClick}
                onMouseMove={handleMouseMove}
            >
                    <motion.div className="showreel-controller"
                        ref={showreelControllerRef}
                        initial ="initial"
                        animate ="animate"
                        exit ="exit"
                    >
                        <motion.div 
                            variants={controlVariants}
                            custom={0}
                            className="exit-toggle"
                            onClick={handleExit}
                            >
                            <span>CLOSE</span>
                        </motion.div>

                        <motion.div
                            variants={controlVariants}
                            custom={1}
                        >
                            <span>{timestamp}</span>
                            <span>/</span>
                            <span>{durationTimestamp}</span>
                        </motion.div>

                        <motion.div 
                                variants={controlVariants}
                                custom={2}
                                className="sound-toggle" 
                                onClick={(e) => handleSoundClick(e)}
                            >
                            <span>
                                SOUND
                                <div className={`strikethrough ${!volume ? "strike-active" : ""}`}/>    
                            </span>
                        </motion.div>
                    </motion.div>

                <div className="con-vim-wrapper"
                    ref={videoWrapperRef}
                >
                    <Vimeo 
                        video={videoLink}
                        className="project-video"
                        height= {"100%"}
                        controls={false}
                        volume={volume}
                        paused={!playVideo}
                        onPlaying={handlePlayingStart}
                        onPause={handlePlayingStopped}
                        onError={handlePlayingStopped}
                        onTimeUpdate={handleTimeUpdate}
                        // loop={true}
                        dnt={true}
                        onReady={player => {playerRef.current = player}}
                    />
                </div>
            </div>
        </>
    )
}

export default Showreel;